import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiArrowLeft,
  FiBookOpen,
  FiClock,
  FiRefreshCw,
  FiSearch,
  FiTarget,
} from 'react-icons/fi';
import { fetchArticleHistory } from './api/articleHistoryAPI';
import { trackVocabHistoryOpened } from './vocabAnalytics';
import {
  checkTokenExpiration,
  toastdisplay,
} from '../../../utils/utils';
import '../GRE.css';

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'accuracy_desc', label: 'Highest accuracy' },
  { value: 'accuracy_asc', label: 'Lowest accuracy' },
];

const DIFFICULTY_OPTIONS = [
  { value: '', label: 'All difficulties' },
  { value: 'easy', label: 'Easy' },
  { value: 'medium', label: 'Medium' },
  { value: 'hard', label: 'Hard' },
];

const formatCompletedDate = (value) => {
  if (!value) return 'Unknown date';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown date';
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatAccuracy = (accuracy) => {
  if (accuracy == null) return '—';
  const pct = accuracy <= 1 ? accuracy * 100 : accuracy;
  return `${Math.round(pct)}%`;
};

export default function ArticleHistory() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [difficulty, setDifficulty] = useState('');
  const [sort, setSort] = useState('newest');
  const [searchInput, setSearchInput] = useState('');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', success: true });

  useEffect(() => {
    trackVocabHistoryOpened();
  }, []);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchArticleHistory({
        page,
        pageSize,
        difficulty: difficulty || undefined,
        q: q || undefined,
        sort,
      });

      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      if (err?.status === 401) {
        await checkTokenExpiration(
          { ok: false, status: 401 },
          () => {},
          (opts) => toastdisplay(setToast, opts)
        );
      }
      setError(err?.message || 'Failed to load article history');
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, difficulty, q, sort]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const openArticle = (publicCode, mode = 'reread') => {
    if (!publicCode) return;
    const path =
      mode === 'exercises'
        ? `/exams/gre/verbal/vocabulary/dashboard/?article=${publicCode}&source=history&step=exercise`
        : `/exams/gre/verbal/vocabulary/dashboard/?article=${publicCode}&source=history`;
    navigate(path);
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setPage(1);
    setQ(searchInput.trim());
  };

  return (
    <div className="vocabulary_mastery article_history_page">
      {toast.show && (
        <div
          className={`toast-notification ${toast.success ? 'success' : 'error'}`}
        >
          {toast.message}
        </div>
      )}

      <div className="article_history_header">
        <button
          type="button"
          className="article_history_back"
          onClick={() =>
            navigate('/exams/gre/', {
              state: { activeCategory: 'vocabularymastery' },
            })
          }
        >
          <FiArrowLeft /> Back to Vocabulary
        </button>
        <h1 className="article_history_title">Article history</h1>
        <p className="article_history_subtitle">
          Revisit articles you have already completed and redo their exercises
          for revision.
        </p>
      </div>

      <form className="article_history_filters" onSubmit={handleSearchSubmit}>
        <div className="article_history_search">
          <FiSearch />
          <input
            type="search"
            placeholder="Search by title…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            aria-label="Search articles by title"
          />
        </div>
        <select
          value={difficulty}
          onChange={(e) => {
            setPage(1);
            setDifficulty(e.target.value);
          }}
          aria-label="Filter by difficulty"
        >
          {DIFFICULTY_OPTIONS.map((opt) => (
            <option key={opt.value || 'all'} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <select
          value={sort}
          onChange={(e) => {
            setPage(1);
            setSort(e.target.value);
          }}
          aria-label="Sort articles"
        >
          {SORT_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <button type="submit" className="article_history_search_btn">
          Search
        </button>
      </form>

      {loading && (
        <div className="article_history_loading">
          <div className="loading-spinner"></div>
          <p>Loading your completed articles…</p>
        </div>
      )}

      {!loading && error && (
        <div className="article_history_empty">
          <p>{error}</p>
          <button
            type="button"
            className="vocabulary_game_configure_btn"
            onClick={loadHistory}
          >
            Try again
          </button>
        </div>
      )}

      {!loading && !error && items.length === 0 && (
        <div className="article_history_empty">
          <FiBookOpen size={48} />
          <h2>No articles to revise yet</h2>
          <p>Complete today&apos;s reading first, then come back to revise.</p>
          <button
            type="button"
            className="vocabulary_game_configure_btn"
            onClick={() =>
              navigate('/exams/gre/', {
                state: { activeCategory: 'vocabularymastery' },
              })
            }
          >
            Go to today&apos;s articles
          </button>
        </div>
      )}

      {!loading && !error && items.length > 0 && (
        <>
          <p className="article_history_count">
            {total} completed article{total === 1 ? '' : 's'}
          </p>
          <div className="article_history_list">
            {items.map((item) => (
              <div
                key={item.public_code}
                className={`article_history_card vocabulary_article_preview${
                  item.is_revised ? ' article_history_card--revised' : ''
                }`}
              >
                <div className="vocabulary_preview_content">
                  <div className="article_history_card_header">
                    <h4 className="vocabulary_preview_title article_history_card_title">
                      {item.title}
                    </h4>
                    <div className="article_history_card_tags">
                      {item.is_revised && (
                        <span className="article_history_revised_badge">
                          <FiRefreshCw size={12} /> Revised
                          {item.revision_count > 1
                            ? ` ×${item.revision_count}`
                            : ''}
                        </span>
                      )}
                      {item.difficulty_level && (
                        <span
                          className={`article_history_difficulty vocabulary_preview_difficulty ${item.difficulty_level}`}
                        >
                          {item.difficulty_level}
                        </span>
                      )}
                    </div>
                  </div>
                  {item.excerpt && (
                    <p className="vocabulary_preview_excerpt">{item.excerpt}</p>
                  )}
                  <div className="vocabulary_preview_badges">
                    <span className="vocabulary_preview_badge">
                      <FiClock size={14} /> Completed{' '}
                      {formatCompletedDate(item.completed_at)}
                    </span>
                    {item.is_revised && item.last_revised_at && (
                      <span className="vocabulary_preview_badge article_history_revised_meta">
                        <FiRefreshCw size={14} /> Last revised{' '}
                        {formatCompletedDate(item.last_revised_at)}
                      </span>
                    )}
                    <span className="vocabulary_preview_badge">
                      <FiTarget size={14} /> Acc. {formatAccuracy(item.accuracy)}
                    </span>
                    <span className="vocabulary_preview_badge">
                      <FiBookOpen size={14} /> {item.words_learned || 0} words
                    </span>
                    {item.estimated_reading_time != null && (
                      <span className="vocabulary_preview_badge">
                        <FiClock size={14} /> {item.estimated_reading_time} min
                      </span>
                    )}
                  </div>
                </div>
                <div className="article_history_actions">
                  <button
                    type="button"
                    className="vocabulary_preview_btn"
                    onClick={() => openArticle(item.public_code, 'reread')}
                  >
                    <FiBookOpen size={16} /> Reread
                  </button>
                  <button
                    type="button"
                    className="vocabulary_preview_btn completed"
                    onClick={() => openArticle(item.public_code, 'exercises')}
                  >
                    <FiRefreshCw size={16} /> Redo exercises
                  </button>
                </div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="article_history_pagination">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <span>
                Page {page} / {totalPages}
              </span>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
